/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo, useState } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { showToast } from '@/features/toast/toastSlice';
import { useParams } from 'react-router-dom';
import ConfirmGenerateAIDialog from '@/components/dialogs/ConfirmGenerateAIDialog';
import { useCreateAIWorkflowMutation } from '@/features/aiWorkflow/aiWorkflowApi';
import {
	AI_WORKFLOW_DEPENDENCIES,
	AI_WORKFLOW_STATUSES,
	AI_WORKFLOW_TYPES,
} from '@/features/aiWorkflow/constants';
import useGetAIMessages from '@/hooks/useGetAIMessages';
import useGetAIWorkflows from '@/hooks/useGetAIWorkflows';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import GenerateMenuItemRow from '@/components/buttons/GenerateAITemplateMenuButton/GenerateMenuItemRow';
import { getMissingDependenciesMessage } from '@/components/buttons/GenerateAITemplateMenuButton/helpers';

const AiTemplateMenuItem = ({ workflowType, onCloseMenu }) => {
	const dispatch = useDispatch();
	const { dealId } = useParams();

	const [confirmationOpen, setConfirmationOpen] = useState(false);

	const title = AI_WORKFLOW_TYPES[workflowType];
	const dependencies = AI_WORKFLOW_DEPENDENCIES[workflowType] ?? [];

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });

	const { getFeatureFlags } = useFeatureFlagContext();
	const useUnlayerTemplate = Boolean(getFeatureFlags('unlayerAiEmailTemplates')?.active);

	const { getLatestSummaries, isLoading, isUninitialized, isError, isSuccess } = useGetAIMessages(
		{ dealId },
	);

	const latestSummaries = useMemo(
		() => getLatestSummaries(dependencies),
		[isLoading, isUninitialized, isError, isSuccess, dependencies, getLatestSummaries],
	);

	const { getLatestWorkflow, isLoading: isWorkflowsLoading } = useGetAIWorkflows({ dealId });
	const latestWorkflow = getLatestWorkflow(workflowType);

	const [createAIWorkflow, { isLoading: isCreatingWorkflow }] = useCreateAIWorkflowMutation({
		fixedCacheKey: workflowType,
	});

	const missingDependencies = useMemo(
		() => dependencies.filter((dependency) => !latestSummaries[dependency]),
		[dependencies, latestSummaries],
	);

	const isGenerating =
		latestWorkflow &&
		![AI_WORKFLOW_STATUSES.ERROR, AI_WORKFLOW_STATUSES.SUCCESS].includes(
			latestWorkflow?.status,
		);

	const disabled =
		isLoading ||
		isWorkflowsLoading ||
		isGenerating ||
		isCreatingWorkflow ||
		missingDependencies.length > 0 ||
		!userQuery.data;

	const disabledMessage = isLoading
		? 'Loading messages ...'
		: isWorkflowsLoading
			? 'Loading workflows ...'
			: isGenerating
				? `${title} workflow in progress ...`
				: isCreatingWorkflow
					? 'Creating workflow ...'
					: missingDependencies.length > 0
						? getMissingDependenciesMessage(missingDependencies)
						: '';

	const handleOpenConfirm = () => {
		if (disabled) return;
		setConfirmationOpen(true);
	};

	const handleCreateWorkflow = async (emailUser) => {
		const aiMessageIds = Object.values(latestSummaries).map((message) => message.id);

		await createAIWorkflow({
			dealId,
			workflowType,
			aiMessageIds,
			emailUser,
			useUnlayerTemplate,
		})
			.unwrap()
			.then(() => {
				dispatch(
					showToast({
						message: `${title} generation in process!${
							emailUser
								? ' You will receive an email when the workflow is complete.'
								: ''
						}`,
						success: true,
					}),
				);
			})
			.catch((err) => {
				console.log('Error creating workflow', err);
				dispatch(
					showToast({
						message: `Error creating ${title} workflow; please try again.`,
						error: true,
					}),
				);
			});
		onCloseMenu?.();
	};

	return (
		<>
			<GenerateMenuItemRow
				title={title}
				icon={<EmailIcon fontSize='small' />}
				message={disabledMessage}
				isBusy={isGenerating || isCreatingWorkflow}
				showWarning={missingDependencies.length > 0}
				disabled={disabled}
				onClick={handleOpenConfirm}
			/>

			{confirmationOpen && (
				<ConfirmGenerateAIDialog
					workflowType={workflowType}
					confirmationOpen={confirmationOpen}
					setConfirmationOpen={setConfirmationOpen}
					onConfirm={handleCreateWorkflow}
					onClose={() => onCloseMenu?.()}
				/>
			)}
		</>
	);
};
AiTemplateMenuItem.displayName = 'AiTemplateMenuItem';
export default AiTemplateMenuItem;
